import {
	ActionIcon,
	Box,
	Button,
	createStyles,
	Divider,
	FileButton,
	Flex,
	Icon,
	Stack,
	TFCard,
	TFHTMLText,
	TFHTMLTextEditor,
	TFNotifier,
	TFText,
	Tooltip,
} from '@tf/ui';
import { fmt, saveFile, splitObjectId } from '@tf/utils';
import React, { useRef, useState } from 'react';

import {
	useAddCaseRequestFileMutation,
	useIsCaseRequestFileMutating,
	useRemoveCaseRequestFileMutation,
	useRevokeCaseRequestMutation,
	useSendCaseRequestMutation,
	useUpdateCaseRequestTextMutation,
} from '@/core/api/caseRequests';
import { downloadFile, isPreviewableFile } from '@/core/api/files';
import type { CaseRequest as CaseRequestType, CaseStatus, CaseWithData } from '@/core/types/cases';

import { DueDate } from './DueDate';

const MAX_CASE_REQUEST_MANAGER_FILES = 20;
const CASE_REQUEST_MANAGER_FILES_ACCEPT = [
	'image/png',
	'image/jpeg',
	'application/pdf',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(', ');

export const CaseRequest: React.FC<{
	caseRequest: CaseRequestType;
	caseData: CaseWithData;
	livenessCheckResults?: React.ReactNode;
	paymentObjectId?: string;
}> = ({ caseRequest, caseData, livenessCheckResults, paymentObjectId }) => {
	const isEditable = caseData.labels.status === 'ACTIVE' && !caseRequest.labels.sent_at;

	return isEditable ? (
		<CaseRequestEditor
			caseRequest={caseRequest}
			canAttachFiles={caseData.kind !== 'identity_verification'}
			recipientEmail={caseData.labels.recipient_email}
		/>
	) : (
		<CaseRequestView
			caseId={caseData.id}
			caseStatus={caseData.labels.status}
			caseRequest={caseRequest}
			livenessCheckResults={livenessCheckResults}
			paymentObjectId={paymentObjectId}
		/>
	);
};

const CaseRequestEditor: React.FC<{
	caseRequest: CaseRequestType;
	canAttachFiles: boolean;
	recipientEmail?: string;
}> = ({ caseRequest, canAttachFiles, recipientEmail }) => {
	const [editorContent, setEditorContent] = useState(caseRequest.data.request_text);
	const hasRecipientEmail = !!recipientEmail?.trim();

	const updateCaseRequestTextMutation = useUpdateCaseRequestTextMutation();
	const sendCaseRequestMutation = useSendCaseRequestMutation();
	const isFileMutating = useIsCaseRequestFileMutating(caseRequest.id);

	return (
		<CaseRequestLayout
			toolbar={<DueDate caseRequestId={caseRequest.id} date={caseRequest.labels.due_date} isEditable />}
			actions={
				<>
					<Button
						variant="light"
						size="sm"
						disabled={isFileMutating}
						onClick={() => {
							updateCaseRequestTextMutation.mutate({
								caseRequestId: caseRequest.id,
								text: editorContent,
							});
						}}
					>
						Save changes
					</Button>

					<Tooltip label="Add the client email before you send this request" disabled={hasRecipientEmail} withArrow>
						<Box>
							<Button
								size="sm"
								disabled={isFileMutating || !hasRecipientEmail}
								onClick={() => {
									if (!hasRecipientEmail) return;
									// Always save a request text before sending it
									updateCaseRequestTextMutation.mutate(
										{
											caseRequestId: caseRequest.id,
											text: editorContent,
										},
										{
											onSuccess: () => {
												sendCaseRequestMutation.mutate({ caseRequestId: caseRequest.id });
											},
										},
									);
								}}
							>
								Send request
							</Button>
						</Box>
					</Tooltip>
				</>
			}
			content={
				<Stack gap="1.5rem">
					<Stack gap="0.5rem">
						<TFText size="md" fw="600">
							Request message:
						</TFText>
						<TFHTMLTextEditor content={editorContent} onChange={setEditorContent} />
					</Stack>

					{canAttachFiles ? <RequestFilesEditor caseRequest={caseRequest} /> : null}

					{caseRequest.data.action ? (
						<Stack gap="0.5rem">
							<TFText size="md" fw="600">
								Link:
							</TFText>
							{caseRequest.data.action.label}: {caseRequest.data.action.url}
						</Stack>
					) : null}
				</Stack>
			}
		/>
	);
};

const CaseRequestView: React.FC<{
	caseId: string;
	caseStatus: CaseStatus;
	caseRequest: CaseRequestType;
	livenessCheckResults?: React.ReactNode;
	paymentObjectId?: string;
}> = ({ caseId, caseStatus, caseRequest, livenessCheckResults, paymentObjectId }) => {
	const revokeCaseRequestMutation = useRevokeCaseRequestMutation();

	const isRevocable =
		!!caseRequest.labels.sent_at &&
		(caseRequest.labels.status === 'DRAFT' ||
			caseRequest.labels.status === 'SENT' ||
			caseRequest.labels.status === 'VIEWED') &&
		caseStatus === 'ACTIVE';

	return (
		<CaseRequestLayout
			toolbar={<DueDate caseRequestId={caseRequest.id} date={caseRequest.labels.due_date} isEditable={false} />}
			actions={
				isRevocable ? (
					<Button
						size="sm"
						onClick={() => {
							revokeCaseRequestMutation.mutate({
								caseId,
								caseRequestId: caseRequest.id,
								paymentObjectId,
							});
						}}
					>
						Revoke request
					</Button>
				) : null
			}
			content={
				<>
					<MessageContainer
						label="Request message"
						text={caseRequest.data.request_text}
						date={caseRequest.labels.sent_at}
					/>
					{caseRequest.data.action ? (
						<Stack gap="0.5rem" mt="1.5rem">
							<TFText size="md" fw="600">
								Link:
							</TFText>
							{caseRequest.data.action.label}: {caseRequest.data.action.url}
						</Stack>
					) : null}
					{caseRequest.request_files.length > 0 ? (
						<Stack gap="0.5rem" mt="1.5rem">
							<TFText size="md" fw="600">
								Attached files:
							</TFText>
							<CaseFileList files={caseRequest.request_files} />
						</Stack>
					) : null}
					{caseRequest.labels.status === 'REVOKED' ? (
						<>
							<Divider
								sx={({ colors }) => ({
									borderColor: colors.gray[3],
									borderWidth: '0.5px',
								})}
								my="sm"
							/>
							<TFText size="md" fw="600">
								Request has been revoked
							</TFText>
						</>
					) : caseRequest.labels.status === 'UPDATED' ? (
						<>
							<Divider
								sx={({ colors }) => ({
									borderColor: colors.gray[3],
									borderWidth: '0.5px',
								})}
								my="sm"
							/>
							<Stack gap="1rem">
								<MessageContainer
									label="Response message"
									text={caseRequest.data.response_text ?? ''}
									date={caseRequest.meta.responded_at}
								/>
								{/* Files */}
								<CaseFileList files={caseRequest.response_files} />
								{livenessCheckResults}
							</Stack>
						</>
					) : null}
				</>
			}
		/>
	);
};

const RequestFilesEditor: React.FC<{ caseRequest: CaseRequestType }> = ({ caseRequest }) => {
	const resetFileRef = useRef<() => void>(null);

	const addCaseRequestFileMutation = useAddCaseRequestFileMutation();
	const removeCaseRequestFileMutation = useRemoveCaseRequestFileMutation();
	const isPending = addCaseRequestFileMutation.isPending || removeCaseRequestFileMutation.isPending;

	const fileRelations = caseRequest.data.request_files ?? [];
	const isLimitReached = fileRelations.length >= MAX_CASE_REQUEST_MANAGER_FILES;

	const addFile = (file: File | null) => {
		if (!file || isLimitReached) {
			return;
		}

		addCaseRequestFileMutation.mutate(
			{ caseRequestId: caseRequest.id, file, hasFiles: fileRelations.length > 0 },
			{
				onError: () => TFNotifier.error('File upload failed', { id: 'CaseRequestFileUploadError' }),
				onSettled: () => resetFileRef.current?.(),
			},
		);
	};

	const removeFile = (fileId: string) => {
		const { objectName } = splitObjectId(fileId);
		const index = fileRelations.findIndex((relation) => splitObjectId(relation._object).objectName === objectName);
		if (index === -1) {
			return;
		}

		removeCaseRequestFileMutation.mutate(
			{ caseRequestId: caseRequest.id, index },
			{ onError: () => TFNotifier.error('File removal failed', { id: 'CaseRequestFileRemoveError' }) },
		);
	};

	return (
		<Stack gap="0.5rem">
			<TFText size="md" fw="600">
				Attached files:
			</TFText>
			<CaseFileList files={caseRequest.request_files} onDelete={removeFile} isDeleteDisabled={isPending} />
			<FileButton accept={CASE_REQUEST_MANAGER_FILES_ACCEPT} resetRef={resetFileRef} onChange={addFile}>
				{(props) => (
					<Button
						variant="light"
						size="sm"
						{...props}
						loading={addCaseRequestFileMutation.isPending}
						disabled={isPending || isLimitReached}
					>
						Add file
					</Button>
				)}
			</FileButton>
			<TFText size="sm" c="dimmed">
				You can attach up to {MAX_CASE_REQUEST_MANAGER_FILES} files: PNG, JPEG, PDF, DOCX, XLSX.
			</TFText>
		</Stack>
	);
};

const CaseFileList: React.FC<{
	files: CaseRequestType['response_files'];
	onDelete?: (fileId: string) => void;
	isDeleteDisabled?: boolean;
}> = ({ files, onDelete, isDeleteDisabled }) => {
	return (
		<Stack gap="0.5rem">
			{files.map((file) => {
				const canPreview = isPreviewableFile(file.object.meta['@mime']);

				return (
					<Flex key={file.object.id} gap={8} align="center">
						<Button
							variant="light"
							size="sm"
							flex={1}
							onClick={async () => {
								const blob = await downloadFile(file.object.id);
								saveFile(blob, file.object.meta['@filename']);
							}}
							rightSection={<Icon.IconDownload size="18" />}
						>
							{file.object.meta['@filename']}
						</Button>

						{canPreview && <CasePreviewButton fileId={file.object.id} fileName={file.object.meta['@filename']} />}

						{onDelete && (
							<Tooltip label={`Delete ${file.object.meta['@filename']}`} withArrow>
								<ActionIcon
									variant="subtle"
									color="red"
									disabled={isDeleteDisabled}
									onClick={() => onDelete(file.object.id)}
								>
									<Icon.IconTrash size={18} stroke={1.5} />
								</ActionIcon>
							</Tooltip>
						)}
					</Flex>
				);
			})}
		</Stack>
	);
};

const CaseRequestLayout: React.FC<{
	toolbar: React.ReactNode;
	actions: React.ReactNode;
	content: React.ReactNode;
}> = ({ toolbar, actions, content }) => {
	return (
		<TFCard innerPadding="1rem">
			<Stack gap="1rem">
				<Flex align="center" justify="space-between">
					<Box>{toolbar}</Box>
					<Flex gap="0.5rem">{actions}</Flex>
				</Flex>
				<Box>{content}</Box>
			</Stack>
		</TFCard>
	);
};

const useMessageContainerStyles = createStyles(() => {
	return {
		date: {
			color: '#64748B',
			fontWeight: 400,
		},
		markdownContainer: {
			flexGrow: 1,
		},
		markdown: {
			'& > :last-of-type': {
				marginBottom: 0,
			},
		},
	};
});

const MessageContainer = ({ label, text, date }: { label: string; text: string; date?: string }) => {
	const { classes } = useMessageContainerStyles();

	return (
		<Stack gap="0.5rem">
			<Flex align="baseline" gap="1rem">
				<TFText size="md" fw="600">
					{label}
				</TFText>
				<TFText className={classes.date}>
					{date ? `Sent ${fmt.toDate(date, { preset: 'full_date_with_time' })}` : 'Not sent'}
				</TFText>
			</Flex>
			<TFHTMLText className={classes.markdown} text={text} />
		</Stack>
	);
};

const CasePreviewButton: React.FC<{ fileId: string; fileName: string }> = ({ fileId, fileName }) => {
	const handlePreview = React.useCallback(async () => {
		try {
			const blob = await downloadFile(fileId);
			const url = URL.createObjectURL(blob);
			window.open(url, '_blank');
			URL.revokeObjectURL(url);
		} catch (e) {
			console.error('Preview failed:', e);
			TFNotifier.error('File preview failed', {
				id: 'CasePreviewError',
			});
		}
	}, [fileId]);

	return (
		<Tooltip label={`Preview ${fileName}`} withArrow>
			<ActionIcon variant="subtle" color="blue" onClick={handlePreview}>
				<Icon.IconEye size={18} stroke={1.5} />
			</ActionIcon>
		</Tooltip>
	);
};
